import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Button, Typography, Dialog, DialogTitle,
  DialogContent, DialogActions, CircularProgress, IconButton, Slider,
  List, ListItem, ListItemIcon, ListItemText, Menu, MenuItem,
  Icon
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import {
  MoreVert, CallEnd, VolumeUp, VolumeOff, Mic, MicOff, ArticleOutlined, Replay
} from '@mui/icons-material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import SosIcon from '@mui/icons-material/Sos';
import MeetingCard from './MeetingCard';
import VoiceOnlyInput from './MeetingVoiceOnlyInput';
import { AssessmentType } from '../services/assessmentService';
import socketService from '../services/socketService';
import { useSocket } from '../contexts/SocketContext';
import { useGrading } from '../contexts/GradingContext';
import useUserPhoto from '../hooks/useUserPhoto';
import { getGlobalAudio, setGlobalAudioVolume, stopGlobalAudio } from '../utils/streamAudioPlayer';



interface MeetingGridProps {
  assessment: AssessmentType;
  onEndMeeting: (transcript: any[], conversationId: string, roleplayName: string, dateCompleted: string) => void;
}

function normalizeTranscriptText(text: string): string {
  return text
    .replaceAll(/[\r\n]+/g, ' ')
    .replaceAll(/•|\*|[\u2022\u2023\u25E6\u2043\u2219-]/g, '')
    .replaceAll(/\s+/g, ' ')
    .replaceAll(/\s*([,.])\s*/g, '$1 ')
    .replaceAll(/\s{2,}/g, ' ')
    .trim();
}

const HOVER_HIDE_DELAY = 120; // ms

const MeetingGrid: React.FC<MeetingGridProps> = ({ assessment, onEndMeeting }) => {
  const { setGradingState } = useGrading();
  const { isConnected } = useSocket();
  const contactName = assessment.personaName || 'AI Customer';
  const yourName = 'You';
  const { photoUrl } = useUserPhoto();

  // All state hooks
  const [waitingForFirstAI, setWaitingForFirstAI] = useState(true);
  const [callCancled, setCallCancled] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [speaking, setSpeaking] = useState<string | null>(null);
  const [transcript, setTranscript] = useState<{ speaker: string, text: string, timestamp: number }[]>([]);
  const [isTranscriptOpen, setIsTranscriptOpen] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [showEndConfirmation, setShowEndConfirmation] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [lastVolume, setLastVolume] = useState(1);
  const [volume, setVolume] = useState(1);
  const [userMuted, setUserMuted] = useState(false);
  const [blockMic, setBlockMic] = useState(true);
  const [socketError, setSocketError] = useState<string | null>(null);
  const [badState, setBadState] = useState<Boolean>(false)
  const voiceOnlyInputRef = useRef<{ handleSosRecovery: () => void }>(null);
  const [moreMenuAnchor, setMoreMenuAnchor] = useState<null | HTMLElement>(null);

  // Timer
  const [seconds, setSeconds] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Handler for the conversation created event
  useEffect(() => {
    const handleConversationCreated = (data: { conversation_id: string }) => {
      setConversationId(data.conversation_id);
    };

    // Set up the event listener
    socketService.onConversationCreated(handleConversationCreated);

    // Create the conversation if needed
    if (!conversationId && isConnected) {
      const title = `Assessment - ${assessment.title}`;
      socketService.createConversation(
        title,
        undefined,
        undefined,
        undefined,
        undefined,
        assessment.id
      );
    }

    // Clean up the event listener on unmount or dependency change
    return () => {
      socketService.socket?.off('conversation_created', handleConversationCreated);
    };
  }, [conversationId, assessment.id, assessment.title, isConnected]);

  useEffect(() => {
    socketService.onSessionCancled(() => setCallCancled(true))
  }, [])

  // Timer logic (counting up in seconds)
  useEffect(() => {
    if (!waitingForFirstAI) {
      timerRef.current = setInterval(() => {
        setSeconds((s) => s + 1);
      }, 1000);
    }
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [waitingForFirstAI]);

  const formatTime = (totalSeconds: number) => {
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  // Internal helpers
  const handleUserTranscript = (text: string, speaker: string) => {
    setTranscript(prev => [...prev, { speaker, text, timestamp: seconds }]);
    setSpeaking(speaker);
    if (speaker === yourName) setTimeout(() => setSpeaking(null), 2000);
  };

  const handleEndMeeting = () => {
    setGradingState({
      roleplayId: assessment.id,
      roleplayName: assessment.title,
      dateCompleted: new Date().toLocaleDateString(),
      status: 'in-progress'
    });
    if (conversationId) {
      onEndMeeting(
        transcript,
        conversationId,
        assessment.title,
        new Date().toLocaleDateString()
      );
    }
  };

  // Compose grid users array
  const userParts = {
    name: yourName,
    title: 'Sales Account Executive',
    ...(photoUrl ? { image: photoUrl } : {}) // Only add image if photoUrl is truthy
  };
  let contactImage;
  if (assessment.voiceGender === 'Male') {
    contactImage = '/male.png';
  } else if (assessment.voiceGender === 'Female') {
    contactImage = '/female.png';
  } else {
    contactImage = '/temp.jpg';
  }

  const gridUsers = [
    {
      name: contactName,
      title: assessment.personaRole || 'Virtual Customer',
      image: contactImage,
    },
    userParts
  ];

  // MeetingTopBar logic
  const [showVolume, setShowVolume] = useState(false);
  const hoverTimeout = useRef<NodeJS.Timeout | null>(null);

  let micLabel;
  if (blockMic) {
    micLabel = "Speaking";
  } else if (userMuted) {
    micLabel = "Muted";
  } else {
    micLabel = "Mute";
  }

  // Config array for all nav buttons
  const navButtons = [
    ...(badState ? [{
      key: 'sos',
      icon: <SosIcon />,
      label: "Save Our Session",
      color: "#FF5C5C",
      underline: blockMic || userMuted,
      fontWeight: 1000,
      onClick: () => voiceOnlyInputRef.current?.handleSosRecovery(),
    }] : []),
    {
      key: 'mic',
      icon: userMuted || blockMic ? <MicOff /> : <Mic />,
      label: micLabel,
      color: blockMic ? "#008216ff" : userMuted ? "#FF5C5C" : "#6c6c6cff",
      underline: blockMic || userMuted,
      fontWeight: blockMic || userMuted ? 600 : 400,
      onClick: blockMic ? undefined : () => {
        setUserMuted(!userMuted);
        setSocketError(null);
      },
      disabled: blockMic,
    },
    {
      key: 'speaker',
      icon: isMuted ? <VolumeOff /> : <VolumeUp />,
      label: isMuted ? "Muted" : "Speaker",
      color: isMuted ? "#FF5C5C" : "#A1A1AA",
      underline: isMuted,
      fontWeight: isMuted ? 600 : 400,
    },
    {
      key: 'sidebar',
      icon: sidebarCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />,
      label: sidebarCollapsed ? "Expand Goals" : "Collapse Goals",
      color: "#7D5FFF",
      underline: sidebarCollapsed,
      fontWeight: 600,
      onClick: () => setSidebarCollapsed(c => !c),
      disabled: false,
    },
    {
      key: 'transcript',
      icon: <ArticleOutlined />,
      label: "Transcript",
      color: isTranscriptOpen ? "#7D5FFF" : "#A1A1AA",
      underline: isTranscriptOpen,
      fontWeight: isTranscriptOpen ? 600 : 400,
      onClick: () => setIsTranscriptOpen(open => !open),
    },
    {
      key: 'more',
      icon: <MoreVert />,
      label: "More",
      color: "#A1A1AA",
      underline: false,
      fontWeight: 400,
      onClick: (e: any) => setMoreMenuAnchor(e.currentTarget),
      // onClick: () => voiceOnlyInputRef.current?.handleSosRecovery(),
    },
    {
      key: 'end',
      icon: <CallEnd />,
      label: "End",
      color: "#FF5C5C",
      underline: false,
      fontWeight: 600,
      onClick: () => setShowEndConfirmation(true),
    },
  ];

  useEffect(() => {
    setGlobalAudioVolume(volume);
  }, [volume]);

  useEffect(() => {
    if (isMuted) {
      setLastVolume(volume > 0 ? volume : 1);
      setVolume(0);
      const currentAudio = getGlobalAudio();
      if (currentAudio) {
        currentAudio.muted = true;
        currentAudio.volume = 0;
      }
    } else {
      setVolume(lastVolume);
      const currentAudio = getGlobalAudio();
      if (currentAudio) {
        currentAudio.muted = false;
        currentAudio.volume = lastVolume;
      }
    }
    // eslint-disable-next-line
  }, [isMuted]);

  // Audio cleanup
  useEffect(() => {
    return () => {
      stopGlobalAudio();
    };
  }, []);

  // Speaker slider hover/focus logic
  const handleSpeakerEnter = () => {
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    setShowVolume(true);
  };
  const handleSpeakerLeave = () => {
    hoverTimeout.current = setTimeout(() => setShowVolume(false), HOVER_HIDE_DELAY);
  };

  return (
    <Box sx={{ width: '100vw', minHeight: '100vh', py: 4, boxSizing: 'border-box' }}>
      {/* Meeting Top Bar */}
      <Box sx={{ display: 'flex', maxHeight: '4vh', justifyContent: 'space-between', alignItems: 'center', mb: 4, px: 4 }}>
        {/* LEFT: Meeting title and timer */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6">Voice Meeting</Typography>
          <Box
            sx={{
              px: 2,
              py: 0.5,
              borderRadius: 2,
              fontWeight: 500,
              fontSize: "1.15rem",
              letterSpacing: 0.5,
              minWidth: 70,
              textAlign: "center"
            }}
            aria-label="Meeting timer"
          >
            {formatTime(seconds)}
          </Box>
        </Box>
        {/* RIGHT: Nav buttons */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {navButtons.map((btn) => {
            if (btn.key === 'more') {
              return (
                <Box
                  key={btn.key}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    minWidth: 64,
                    cursor: 'pointer',
                    position: 'relative',
                  }}
                  // Instead of onClick={btn.onClick}
                  onClick={btn.onClick}
                  tabIndex={0}
                >
                  <IconButton
                    sx={{
                      color: btn.color,
                      mb: 0.25,
                      fontSize: 24,
                    }}
                  >
                    {btn.icon}
                  </IconButton>
                  <Typography
                    variant="caption"
                    sx={{
                      color: btn.color,
                      fontWeight: btn.fontWeight,
                      fontSize: '0.84rem',
                      letterSpacing: 0.2,
                      textAlign: 'center',
                    }}
                  >
                    {btn.label}
                  </Typography>
                </Box>
              );
            }
            if (btn.key === 'speaker') {
              return (
                <Box
                  key={btn.key}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    minWidth: 64,
                    cursor: 'pointer',
                    position: 'relative',
                  }}
                  onMouseEnter={handleSpeakerEnter}
                  onMouseLeave={handleSpeakerLeave}
                  tabIndex={0}
                >
                  <IconButton
                    sx={{
                      color: btn.color,
                      mb: 0.25,
                      fontSize: 24,
                    }}
                    aria-label={isMuted ? "Unmute Speaker" : "Mute Speaker"}
                    onClick={() => setIsMuted(!isMuted)}
                  >
                    {btn.icon}
                  </IconButton>
                  <Typography
                    variant="caption"
                    sx={{
                      color: btn.color,
                      fontWeight: btn.fontWeight,
                      fontSize: '0.84rem',
                      letterSpacing: 0.2,
                      textAlign: 'center',
                    }}
                  >
                    {btn.label}
                  </Typography>
                  {btn.underline && (
                    <Box
                      sx={{
                        height: 3,
                        width: 28,
                        mt: 0.5,
                        bgcolor: btn.color,
                        borderRadius: 2,
                      }}
                    />
                  )}
                  {showVolume && (
                    <Box
                      onMouseEnter={handleSpeakerEnter}
                      onMouseLeave={handleSpeakerLeave}
                      sx={{
                        position: 'absolute',
                        top: '100%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        mt: 1,
                        px: 2,
                        py: 1,
                        borderRadius: 2,
                        zIndex: 9999,
                        minWidth: 120,
                      }}
                    >
                      <Slider
                        value={volume * 100}
                        onChange={(_, v) => setVolume((v) / 100)}
                        min={0}
                        max={100}
                        size="small"
                        disabled={isMuted}
                        aria-label="Volume"
                        sx={{ width: 100 }}
                      />
                    </Box>
                  )}
                </Box>
              );
            }
            return (
              <Box
                key={btn.key}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  minWidth: 64,
                  cursor: btn.onClick ? 'pointer' : 'default',
                  position: 'relative',
                }}
                onClick={btn.onClick}
                tabIndex={0}
              >
                <IconButton
                  sx={{
                    color: btn.color,
                    mb: 0.25,
                    fontSize: 24,
                  }}
                  disabled={btn.disabled}
                >
                  {btn.icon}
                </IconButton>
                <Typography
                  variant="caption"
                  sx={{
                    color: btn.color,
                    fontWeight: btn.fontWeight,
                    fontSize: '0.84rem',
                    letterSpacing: 0.2,
                    textAlign: 'center',
                  }}
                >
                  {btn.label}
                </Typography>
                {btn.underline && (
                  <Box
                    sx={{
                      height: 3,
                      width: 28,
                      mt: 0.5,
                      bgcolor: btn.color,
                      borderRadius: 2,
                    }}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
      <Menu
        anchorEl={moreMenuAnchor}
        open={Boolean(moreMenuAnchor)}
        onClose={() => setMoreMenuAnchor(null)}
      >
        <MenuItem>
          <IconButton 
            onClick={() => {
              setMoreMenuAnchor(null);
              voiceOnlyInputRef.current?.handleSosRecovery()}
            }
            sx={{
              display:'flex', 
              flexDirection:'column',
              mb: 0.25,
              fontSize: 24,
            }}
          >
            <Replay/>
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.84rem',
                letterSpacing: 0.2,
                textAlign: 'center',
              }}
            >
              Repeat
            </Typography>
          </IconButton>
        </MenuItem>
        {/* Add more dropdown menu items as needed */}
      </Menu>
      {/* Main meeting area */}
      <Box sx={{ display: 'flex', width: '100vw', height: '76vh', alignItems: 'flex-start', position: 'absolute' }}>
        <Box
          sx={{
            width: sidebarCollapsed ? '100vw' : 'calc(100vw - 10vw)',
            transition: 'width 0.3s',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            height: '100%',
          }}
        >
          {waitingForFirstAI ? (
            <Box
              sx={{
                width: '100%',
                height: '60%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  borderRadius: 4,
                  boxShadow: 4,
                  p: 6,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 20,
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <CircularProgress size={48} />
                  <Typography variant="h5" sx={{ color: 'text.secondary' }}>
                    Connecting to your meeting...
                  </Typography>
                </Box>
              </Box>
            </Box>
          ): callCancled ? (
            <Box
              sx={{
                width: '100%',
                height: '60%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  borderRadius: 4,
                  boxShadow: 4,
                  p: 6,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  zIndex: 20,
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                  <IconButton
                    sx={{
                      color: "red",
                      mb: 0.25,
                      fontSize: 24,
                    }}
                  >
                    <CallEnd/>
                  </IconButton>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: '0.84rem',
                      letterSpacing: 0.2,
                      textAlign: 'center',
                    }}
                  >
                    The meeting has been cancled
                  </Typography>
                </Box>
              </Box>
            </Box>
          ): (
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                height: '60%',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '32px',
                justifyContent: 'center',
                alignItems: 'flex-start',
              }}
            >
              {gridUsers.map((user, idx) => (
                <Box
                  key={user.name + idx}
                  sx={{
                    width: '35vh',
                    height: '35vh',
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <MeetingCard
                    name={user.name}
                    title={user.title}
                    image={user.image}
                    isSpeaking={speaking === user.name}
                  />
                </Box>
              ))}
            </Box>
          )}

          {/* Voice input controls */}
          <VoiceOnlyInput
            ref={voiceOnlyInputRef}
            userName={yourName}
            contactName={contactName}
            assessment={assessment}
            onTranscript={handleUserTranscript}
            onSpeaking={setSpeaking}
            conversationId={conversationId}
            isMuted={isMuted}
            setUserMuted={setUserMuted}
            volume={volume}
            userMuted={userMuted}
            blockMic={blockMic}
            setBlockMic={setBlockMic}
            onFirstAIVoice={() => setWaitingForFirstAI(false)}
            waitingForFirstAI={waitingForFirstAI}
            socketError={socketError}
            setSocketError={setSocketError}
            setBadState={setBadState}
          />
        </Box>
        {/* Sidebar */}
        <Box
          sx={{
            width: sidebarCollapsed ? 48 : '10vw',
            minWidth: sidebarCollapsed ? 48 : '25vw',
            maxWidth: sidebarCollapsed ? 48 : '25vw',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            transition: 'width 0.3s ease-in-out',
            overflow: 'hidden',
            flexShrink: 0,
            position: 'relative',
          }}
        >
          {!sidebarCollapsed && (
            <Box
              sx={{
                p: 3,
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                height: '90%',
                borderRadius: 2,
                boxShadow: '0 8px 20px rgba(0,0,0,0.08)',
                border: '1px solid rgba(0,0,0,0.08)',
                mx: 1,
                my: 1,
              }}
            >
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, fontSize: '1.1rem' }}>
                Meeting Goals
              </Typography>
              <List
                sx={{
                  mb: 2,
                  p: 0,
                  overflow: 'auto',
                  height: '100%',
                  bgcolor: 'transparent',
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    borderRadius: 999,
                  },
                }}
              >
                {assessment.goals.map((goal, idx) => (
                  <ListItem
                    key={goal}
                    disableGutters
                    sx={{
                      alignItems: 'flex-start',
                      px: 0,
                      py: 1.25,
                      '&:not(:last-child)': { borderBottom: 'none' },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32, mt: '4px' }}>
                      <RadioButtonUncheckedIcon sx={{ color: '#7D5FFF', fontSize: 18 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 500,
                              fontSize: '.75rem',
                              lineHeight: 1.35,
                              wordBreak: 'break-word',
                              whiteSpace: 'normal',
                            }}
                          >
                            {goal}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: '0.65rem',
                              color: '#4CAF50',
                              fontStyle: 'italic',
                              mt: 0.5,
                              display: 'block',
                            }}
                          >
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Box>
        {/* Transcript Panel */}
        {isTranscriptOpen && (
          <Box
            sx={{
              position: 'fixed',
              left: 0,
              right: sidebarCollapsed ? 0 : 320,
              bottom: 0,
              width: sidebarCollapsed ? '100vw' : '75vw',
              boxShadow: 6,
              zIndex: 1300,
              p: 0,
              height: '25vh',
              display: 'flex',
              flexDirection: 'column',
              transition: 'right 0.3s, width 0.3s',
            }}
          >
            <Box
              sx={{
                px: 3,
                pb: 2,
                pt: 0,
                flex: 1,
                overflowY: 'auto',
                bgcolor: 'transparent',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              {transcript.length === 0 ? (
                <Typography>No conversation yet.</Typography>
              ) : (
                transcript.map((line, i) => {
                  const isUser = line.speaker === yourName;
                  return (
                    <Box
                      key={`${line.speaker}-${line.text}-${i}`}
                      sx={{
                        display: 'flex',
                        flexDirection: isUser ? 'row' : 'row-reverse',
                        alignItems: 'flex-end',
                        gap: 1.5,
                        userSelect: 'none',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-end', mb: '-4px' }}>
                        <PersonIcon sx={{ fontSize: 26 }} />
                      </Box>
                      <Box
                        sx={{
                          borderRadius: 2,
                          px: 2,
                          py: 1,
                          maxWidth: '75%',
                          minWidth: 80,
                          boxShadow: isUser ? 2 : 1,
                          alignSelf: 'flex-end',
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 500,
                            fontSize: '1rem',
                            textAlign: isUser ? 'right' : 'left',
                            whiteSpace: 'pre-line',
                            wordBreak: 'break-word',
                          }}
                        >
                          {normalizeTranscriptText(line.text)}
                        </Typography>
                        <Typography variant="caption">
                          {line.speaker} &nbsp;|&nbsp; {formatTime(line.timestamp)}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })
              )}
            </Box>
          </Box>
        )}
      </Box>
      {/* End Meeting Confirmation Dialog */}
      <Dialog
        open={showEndConfirmation}
        onClose={() => setShowEndConfirmation(false)}
        aria-labelledby="end-meeting-dialog-title"
      >
        <DialogTitle id="end-meeting-dialog-title">
          End Meeting and Start Grading
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to end this meeting? This will trigger the grading process for your performance.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowEndConfirmation(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={() => {
            setShowEndConfirmation(false);
            handleEndMeeting();
          }} color="error" variant="contained">
            End Meeting & Grade
          </Button>
        </DialogActions>
      </Dialog>
      <textarea
        value={transcript.map(line => `${line.speaker}: ${line.text}`).join('\n')}
        readOnly
        style={{ display: 'none' }}
        aria-label="Meeting transcript"
      />
    </Box>
  );
};

export default MeetingGrid;